import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { api } from "@/App";
import { toast } from "sonner";
import { Loader2, TrendingUp, AlertTriangle, CheckCircle, Calendar, Target, Car } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function SeasonalityPage() {
  const [loading, setLoading] = useState(true);
  const [seasonalityData, setSeasonalityData] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [viewType, setViewType] = useState("vn"); // vn, vo
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // Mois courant par défaut

  // Display: July-August is a merged period (indices 6 = "Juil-Août"). No standalone August entry.
  const monthNamesShort = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil-Août", "Sep", "Oct", "Nov", "Déc"];
  // Month numbers effectively selectable (August absorbed into July -> not shown separately).
  const monthNumsShown = [1, 2, 3, 4, 5, 6, 7, 9, 10, 11, 12];
  // For KPI section: map selected month to its label (Aug maps to Juil-Août)
  const getMonthFullLabel = (m) => {
    const map = { 1: "Janvier", 2: "Février", 3: "Mars", 4: "Avril", 5: "Mai", 6: "Juin",
                  7: "Juillet-Août", 8: "Juillet-Août", 9: "Septembre",
                  10: "Octobre", 11: "Novembre", 12: "Décembre" };
    return map[m] || "";
  };

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [seasonalityRes, objectivesRes, concessionsRes] = await Promise.all([
        api.get("/seasonality"),
        api.get("/objectives", { params: { year: parseInt(selectedYear) } }),
        api.get("/concessions")
      ]);
      
      setSeasonalityData(seasonalityRes.data);
      setObjectives(objectivesRes.data);
      setConcessions(concessionsRes.data.filter(c => c.is_active));
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const getSeasonalityForConcession = (concessionName, month) => {
    return seasonalityData.find(d => d.concession_name === concessionName && d.month === month);
  };

  // Calculer le budget mensuel = budget annuel * pourcentage saisonnalité / 100
  const getMonthlyBudget = (concessionName, concessionId, month) => {
    const concession = concessions.find(c => c.id === concessionId);
    const seasonality = getSeasonalityForConcession(concessionName, month);
    
    if (!concession || !seasonality) return { vn: 0, vo: 0 };
    
    return {
      vn: Math.round((concession.budget_vn || 0) * (seasonality.vn_percent || 0) / 100),
      vo: Math.round((concession.budget_vo || 0) * (seasonality.vo_percent || 0) / 100)
    };
  };

  const getObjectivesForConcession = (concessionId, month) => {
    const objs = objectives.filter(o => o.concession_id === concessionId && o.month === month);
    return {
      vn: objs.reduce((sum, o) => sum + (o.vn_target || 0), 0),
      vo: objs.reduce((sum, o) => sum + (o.vo_target || 0), 0),
      count: objs.length
    };
  };

  const getCellStyle = (objective, budget) => {
    if (budget === 0 || budget === undefined) return { bg: "bg-gray-100", text: "text-gray-400" };
    if (objective === 0 || objective === undefined) return { bg: "bg-red-100", text: "text-red-700" };
    
    const ratio = objective / budget;
    
    if (ratio >= 1) {
      return { bg: "bg-green-100", text: "text-green-700" };
    } else if (ratio >= 0.9) {
      return { bg: "bg-yellow-100", text: "text-yellow-700" };
    } else {
      return { bg: "bg-red-100", text: "text-red-700" };
    }
  };

  const calculateTotals = () => {
    let totalBudget = 0;
    let totalObjective = 0;
    
    concessions.forEach(concession => {
      [1,2,3,4,5,6,7,8,9,10,11,12].forEach(month => {
        const monthlyBudget = getMonthlyBudget(concession.name, concession.id, month);
        const objectiveData = getObjectivesForConcession(concession.id, month);
        
        if (viewType === "vn") {
          totalBudget += monthlyBudget.vn;
          totalObjective += objectiveData.vn;
        } else {
          totalBudget += monthlyBudget.vo;
          totalObjective += objectiveData.vo;
        }
      });
    });
    
    return { totalBudget, totalObjective, ecart: totalObjective - totalBudget };
  };

  // Calcul des KPIs consolidés pour le mois sélectionné
  const calculateMonthlyKPIs = () => {
    let totalBudgetVN = 0;
    let totalObjectiveVN = 0;
    let totalBudgetVO = 0;
    let totalObjectiveVO = 0;
    let concessionDetails = [];
    
    // If user selected July or August, aggregate both (summer merged period) for non-Fleet concessions
    const monthsToAgg = (selectedMonth === 7 || selectedMonth === 8) ? [7, 8] : [selectedMonth];
    
    concessions.forEach(concession => {
      const isFleet = concession.name === 'FLEET' || concession.is_fleet === true;
      const monthsForConc = isFleet ? [selectedMonth] : monthsToAgg;
      let budgetVN = 0, budgetVO = 0, objVN = 0, objVO = 0;
      monthsForConc.forEach(m => {
        const mb = getMonthlyBudget(concession.name, concession.id, m);
        const od = getObjectivesForConcession(concession.id, m);
        budgetVN += mb.vn;
        budgetVO += mb.vo;
        objVN += od.vn;
        objVO += od.vo;
      });
      totalBudgetVN += budgetVN;
      totalObjectiveVN += objVN;
      totalBudgetVO += budgetVO;
      totalObjectiveVO += objVO;
      concessionDetails.push({
        name: concession.name,
        budgetVN, objectiveVN: objVN, budgetVO, objectiveVO: objVO,
        ecartVN: objVN - budgetVN, ecartVO: objVO - budgetVO
      });
    });
    
    const percentVN = totalBudgetVN > 0 ? Math.round((totalObjectiveVN / totalBudgetVN) * 100) : 0;
    const percentVO = totalBudgetVO > 0 ? Math.round((totalObjectiveVO / totalBudgetVO) * 100) : 0;
    
    return {
      vn: { budget: totalBudgetVN, objective: totalObjectiveVN, ecart: totalObjectiveVN - totalBudgetVN, percent: percentVN },
      vo: { budget: totalBudgetVO, objective: totalObjectiveVO, ecart: totalObjectiveVO - totalBudgetVO, percent: percentVO },
      details: concessionDetails
    };
  };

  const getStatusColor = (percent) => {
    if (percent >= 100) return { bg: "bg-green-500", text: "text-green-700", light: "bg-green-100" };
    if (percent >= 90) return { bg: "bg-amber-500", text: "text-amber-700", light: "bg-amber-100" };
    return { bg: "bg-red-500", text: "text-red-700", light: "bg-red-100" };
  };

  if (loading) {
    return (
      <Layout title="Saisonnalité vs Objectifs">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-red-600" />
        </div>
      </Layout>
    );
  }

  const totals = calculateTotals();
  const monthlyKPIs = calculateMonthlyKPIs();
  const vnStatus = getStatusColor(monthlyKPIs.vn.percent);
  const voStatus = getStatusColor(monthlyKPIs.vo.percent);

  return (
    <Layout title="Saisonnalité vs Objectifs">
      <div className="space-y-6">
        
        {/* ========== SECTION 1: KPIs CONSOLIDÉS DU MOIS ========== */}
        <div className="bg-gradient-to-br from-[#212E38] to-[#1a252e] rounded-2xl p-6 text-white" data-testid="monthly-kpis-section">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-white/10 p-3 rounded-xl">
                <Calendar className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  Vision consolidée - {getMonthFullLabel(selectedMonth)} {selectedYear}
                </h2>
                <p className="text-gray-400 text-sm">Comparaison objectifs vs saisonnalité historique</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-24 bg-white/10 border-white/20 text-white" data-testid="kpi-year-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2024">2024</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2027">2027</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-36 bg-white/10 border-white/20 text-white" data-testid="month-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthNumsShown.map((mNum) => (
                    <SelectItem key={mNum} value={mNum.toString()}>{getMonthFullLabel(mNum)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* KPIs Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* VN Card */}
            <div className="bg-white/5 backdrop-blur rounded-xl p-5 border border-white/10" data-testid="kpi-vn">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Car className="w-5 h-5 text-blue-400" />
                  <span className="font-semibold">Véhicules Neufs (VN)</span>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-bold ${vnStatus.light} ${vnStatus.text}`}>
                  {monthlyKPIs.vn.percent}%
                </span>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Objectifs fixés</span>
                  <span className="text-2xl font-bold">{monthlyKPIs.vn.objective}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Budget saisonnalité</span>
                  <span className="text-lg text-gray-300">{Math.round(monthlyKPIs.vn.budget)}</span>
                </div>
                
                {/* Progress bar */}
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${vnStatus.bg} transition-all duration-500`}
                    style={{ width: `${Math.min(monthlyKPIs.vn.percent, 100)}%` }}
                  />
                </div>
                
                <div className={`text-center py-2 rounded-lg ${monthlyKPIs.vn.ecart >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                  <span className={`font-bold ${monthlyKPIs.vn.ecart >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    Écart: {monthlyKPIs.vn.ecart >= 0 ? '+' : ''}{monthlyKPIs.vn.ecart} véhicules
                  </span>
                </div>
              </div>
            </div>
            
            {/* VO Card */}
            <div className="bg-white/5 backdrop-blur rounded-xl p-5 border border-white/10" data-testid="kpi-vo">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Car className="w-5 h-5 text-indigo-400" />
                  <span className="font-semibold">Véhicules Occasion (VO)</span>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-bold ${voStatus.light} ${voStatus.text}`}>
                  {monthlyKPIs.vo.percent}%
                </span>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Objectifs fixés</span>
                  <span className="text-2xl font-bold">{monthlyKPIs.vo.objective}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Budget saisonnalité</span>
                  <span className="text-lg text-gray-300">{Math.round(monthlyKPIs.vo.budget)}</span>
                </div>
                
                {/* Progress bar */}
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${voStatus.bg} transition-all duration-500`}
                    style={{ width: `${Math.min(monthlyKPIs.vo.percent, 100)}%` }}
                  />
                </div>
                
                <div className={`text-center py-2 rounded-lg ${monthlyKPIs.vo.ecart >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                  <span className={`font-bold ${monthlyKPIs.vo.ecart >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    Écart: {monthlyKPIs.vo.ecart >= 0 ? '+' : ''}{monthlyKPIs.vo.ecart} véhicules
                  </span>
                </div>
              </div>
            </div>
            
            {/* Global Status Card */}
            <div className="bg-white/5 backdrop-blur rounded-xl p-5 border border-white/10" data-testid="kpi-global">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-5 h-5 text-amber-400" />
                <span className="font-semibold">Statut Global</span>
              </div>
              
              <div className="space-y-4">
                <div className="text-center">
                  {(monthlyKPIs.vn.percent >= 100 && monthlyKPIs.vo.percent >= 100) ? (
                    <div className="flex flex-col items-center gap-2">
                      <CheckCircle className="w-12 h-12 text-green-400" />
                      <span className="text-green-400 font-bold text-lg">Objectifs alignés</span>
                      <span className="text-gray-400 text-sm">Les objectifs couvrent la saisonnalité</span>
                    </div>
                  ) : (monthlyKPIs.vn.percent >= 90 && monthlyKPIs.vo.percent >= 90) ? (
                    <div className="flex flex-col items-center gap-2">
                      <AlertTriangle className="w-12 h-12 text-amber-400" />
                      <span className="text-amber-400 font-bold text-lg">Attention requise</span>
                      <span className="text-gray-400 text-sm">Objectifs proches du budget (90-99%)</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <AlertTriangle className="w-12 h-12 text-red-400" />
                      <span className="text-red-400 font-bold text-lg">Écart significatif</span>
                      <span className="text-gray-400 text-sm">Objectifs inférieurs à la saisonnalité</span>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/10">
                  <div className="text-center">
                    <div className="text-xs text-gray-400">Total Objectifs</div>
                    <div className="font-bold">{monthlyKPIs.vn.objective + monthlyKPIs.vo.objective}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-400">Total Budget</div>
                    <div className="font-bold">{Math.round(monthlyKPIs.vn.budget + monthlyKPIs.vo.budget)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ========== SECTION 2: DÉTAIL PAR SITE ========== */}
        
        {/* Header détail par site */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-red-100 p-3 rounded-xl">
              <TrendingUp className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#212E38]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Détail par site
              </h2>
              <p className="text-gray-500 text-sm">Vue annuelle objectifs vs saisonnalité par concession</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Select value={viewType} onValueChange={setViewType}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vn">Véhicules Neufs</SelectItem>
                <SelectItem value="vo">Véhicules Occasion</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <span className="text-gray-500">Légende :</span>
          <span className="px-3 py-1 rounded bg-green-100 text-green-700 flex items-center gap-1">
            <CheckCircle className="w-4 h-4" /> Objectif ≥ Budget
          </span>
          <span className="px-3 py-1 rounded bg-yellow-100 text-yellow-700">Objectif 90-99% du budget</span>
          <span className="px-3 py-1 rounded bg-red-100 text-red-700 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> Objectif &lt; 90% du budget
          </span>
          <span className="px-3 py-1 rounded bg-gray-100 text-gray-400">Pas de données</span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#212E38] text-white">
                  <th className="text-left p-3 font-semibold sticky left-0 bg-[#212E38] z-10 min-w-[180px]">
                    Concession
                  </th>
                  {monthNamesShort.map((month, idx) => (
                    <th key={idx} className="text-center p-2 font-semibold min-w-[90px]">
                      <div>{month}</div>
                      <div className="text-xs font-normal opacity-70">Obj / Budg</div>
                    </th>
                  ))}
                  <th className="text-center p-2 font-semibold bg-gray-800 min-w-[100px]">
                    <div>Total</div>
                    <div className="text-xs font-normal opacity-70">Écart</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {concessions.map((concession, idx) => {
                  let annualBudget = 0;
                  let annualObjective = 0;
                  const isFleet = concession.name === 'FLEET';
                  
                  // Pour FLEET, on affiche par trimestre
                  if (isFleet) {
                    const quarters = [
                      { name: 'T1', months: [1, 2, 3] },
                      { name: 'T2', months: [4, 5, 6] },
                      { name: 'T3', months: [7, 8, 9] },
                      { name: 'T4', months: [10, 11, 12] }
                    ];
                    
                    return (
                      <tr key={concession.id} className="bg-blue-50">
                        <td className="p-3 font-medium text-[#212E38] sticky left-0 bg-blue-50 border-r border-gray-200 text-sm">
                          <div className="flex items-center gap-2">
                            {concession.name}
                            <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded">Trimestriel</span>
                          </div>
                        </td>
                        {quarters.map((quarter, qIdx) => {
                          // Calculer budget et objectif trimestriel
                          let quarterBudget = 0;
                          let quarterObjective = 0;
                          
                          quarter.months.forEach(month => {
                            const monthlyBudget = getMonthlyBudget(concession.name, concession.id, month);
                            const objectiveData = getObjectivesForConcession(concession.id, month);
                            quarterBudget += viewType === "vn" ? monthlyBudget.vn : monthlyBudget.vo;
                            quarterObjective += viewType === "vn" ? objectiveData.vn : objectiveData.vo;
                          });
                          
                          annualBudget += quarterBudget;
                          annualObjective += quarterObjective;
                          
                          const style = getCellStyle(quarterObjective, quarterBudget);
                          const ecart = quarterObjective - quarterBudget;
                          // T3 (Juil-Août fusionné + Sep) prend 2 colonnes, les autres 3
                          const cSpan = quarter.name === 'T3' ? 2 : 3;
                          
                          return (
                            <td 
                              key={quarter.name}
                              colSpan={cSpan}
                              className={`text-center p-2 text-sm ${style.bg} ${style.text} border-x border-gray-200`}
                              title={`${quarter.name}: Objectif: ${quarterObjective} | Budget: ${Math.round(quarterBudget)} | Écart: ${ecart >= 0 ? '+' : ''}${Math.round(ecart)}`}
                            >
                              <div className="font-bold text-base">{quarter.name}</div>
                              <div className="font-semibold">{quarterObjective}</div>
                              <div className="text-xs opacity-70">/ {Math.round(quarterBudget)}</div>
                            </td>
                          );
                        })}
                        <td className="text-center p-2 border-l border-gray-200 bg-blue-100">
                          <div className="font-bold text-[#212E38]">
                            {annualObjective} / {Math.round(annualBudget)}
                          </div>
                          <div className={`text-sm font-semibold ${annualObjective - annualBudget >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {annualObjective - annualBudget >= 0 ? '+' : ''}{Math.round(annualObjective - annualBudget)}
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  
                  // Pour les autres concessions, affichage mensuel normal
                  return (
                    <tr key={concession.id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="p-3 font-medium text-[#212E38] sticky left-0 bg-inherit border-r border-gray-200 text-sm">
                        {concession.name}
                      </td>
                      {monthNumsShown.map(month => {
                        // For non-Fleet: month 7 tile combines July + August data (summer merged)
                        const isCombinedSummer = month === 7 && !isFleet;
                        const aggMonths = isCombinedSummer ? [7, 8] : [month];
                        let budget = 0;
                        let objective = 0;
                        aggMonths.forEach(m => {
                          const monthlyBudget = getMonthlyBudget(concession.name, concession.id, m);
                          const objectiveData = getObjectivesForConcession(concession.id, m);
                          budget += viewType === "vn" ? monthlyBudget.vn : monthlyBudget.vo;
                          objective += viewType === "vn" ? objectiveData.vn : objectiveData.vo;
                        });
                        
                        annualBudget += budget;
                        annualObjective += objective;
                        
                        const style = getCellStyle(objective, budget);
                        const ecart = objective - budget;
                        
                        return (
                          <td 
                            key={month} 
                            className={`text-center p-2 text-sm ${style.bg} ${style.text}`}
                            title={`Objectif: ${objective} | Budget: ${Math.round(budget)} | Écart: ${ecart >= 0 ? '+' : ''}${Math.round(ecart)}`}
                          >
                            <div className="font-semibold">{objective}</div>
                            <div className="text-xs opacity-70">/ {Math.round(budget)}</div>
                          </td>
                        );
                      })}
                      <td className="text-center p-2 border-l border-gray-200 bg-gray-100">
                        <div className="font-bold text-[#212E38]">
                          {annualObjective} / {Math.round(annualBudget)}
                        </div>
                        <div className={`text-sm font-semibold ${annualObjective - annualBudget >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {annualObjective - annualBudget >= 0 ? '+' : ''}{Math.round(annualObjective - annualBudget)}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Footer with totals */}
              <tfoot>
                <tr className="bg-gray-800 text-white font-semibold">
                  <td className="p-3 sticky left-0 bg-gray-800">
                    TOTAL GROUPE
                  </td>
                  {monthNumsShown.map(month => {
                    let monthBudget = 0;
                    let monthObjective = 0;
                    const aggMonths = month === 7 ? [7, 8] : [month];
                    
                    concessions.forEach(concession => {
                      const isFleet = concession.name === 'FLEET' || concession.is_fleet === true;
                      // For Fleet, aggregate over the single month only (they use quarterly rows)
                      const effectiveMonths = isFleet ? [month] : aggMonths;
                      effectiveMonths.forEach(m => {
                        const monthlyBudget = getMonthlyBudget(concession.name, concession.id, m);
                        const objectiveData = getObjectivesForConcession(concession.id, m);
                        
                        if (viewType === "vn") {
                          monthBudget += monthlyBudget.vn;
                          monthObjective += objectiveData.vn;
                        } else {
                          monthBudget += monthlyBudget.vo;
                          monthObjective += objectiveData.vo;
                        }
                      });
                    });
                    
                    const ecart = monthObjective - monthBudget;
                    
                    return (
                      <td key={month} className="text-center p-2">
                        <div>{monthObjective} / {Math.round(monthBudget)}</div>
                        <div className={`text-xs ${ecart >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {ecart >= 0 ? '+' : ''}{Math.round(ecart)}
                        </div>
                      </td>
                    );
                  })}
                  <td className="text-center p-2 bg-gray-700">
                    <div>{totals.totalObjective} / {Math.round(totals.totalBudget)}</div>
                    <div className={`text-sm ${totals.ecart >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {totals.ecart >= 0 ? '+' : ''}{Math.round(totals.ecart)}
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
